import React, { useCallback, useContext, useEffect, useState } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";

import { Alert, Button } from "reactstrap";

import { ProductContext } from "../../../store";

import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";

import LlmsTxtHeader from "../../../components/AiVisibility/LlmsTxt/LlmsTxtHeader";

import LlmsTxtOutputView from "../../../components/AiVisibility/LlmsTxt/LlmsTxtOutputView";

import LlmsTxtProcessing from "../../../components/AiVisibility/LlmsTxt/LlmsTxtProcessing";

import {
	formatLlmsTxtError,
	hasLlmsTxtErrorDetails,
	LLMS_TXT_GENERIC_ERROR,
} from "../../../components/AiVisibility/LlmsTxt/llmsTxtErrorUtils";

import {

	getLlmsTxtJob,

	isLlmsTxtJobComplete,

	isLlmsTxtJobFailed,

	pollLlmsTxtJob,

} from "../../../services/llmsTxtApi";



function FailedJobPanel({ job, fallbackMessage }) {
	const displayError =
		formatLlmsTxtError(job?.error_message) ||
		formatLlmsTxtError(fallbackMessage) ||
		LLMS_TXT_GENERIC_ERROR;
	const showDetails = hasLlmsTxtErrorDetails(job?.error_message, displayError);



	return (

		<Alert color="danger" className="llms-txt__error-alert mt-4 mb-0">

			<p className="mb-0 fw-medium">{displayError}</p>

			{showDetails && (

				<details className="llms-txt__error-details mt-2 mb-0">

					<summary className="small">Technical details</summary>

					<pre className="llms-txt__error-raw small mb-0 mt-2">{job.error_message}</pre>

				</details>

			)}

		</Alert>

	);

}



const AiVisibilityLlmsTxtView = () => {

	const navigate = useNavigate();

	const [searchParams] = useSearchParams();

	const jobId = searchParams.get("jobId");

	const { currentWebsiteId, userLoader, websiteLoader } = useContext(ProductContext);



	const [job, setJob] = useState(null);

	const [phase, setPhase] = useState("idle");

	const [initialLoading, setInitialLoading] = useState(true);

	const [error, setError] = useState(null);

	const [processingStatus, setProcessingStatus] = useState("pending");

	const [processingLabel, setProcessingLabel] = useState(null);

	const [processingProgress, setProcessingProgress] = useState(null);

	const [pagesTotal, setPagesTotal] = useState(null);

	const [pagesExtracted, setPagesExtracted] = useState(null);

	const [lastStage, setLastStage] = useState(null);



	const applyStatusTick = useCallback((statusData) => {

		if (statusData?.status_label) {

			setProcessingLabel(statusData.status_label);

		}

		if (statusData?.status) {

			setProcessingStatus(statusData.status);

		}

		if (statusData?.progress != null) {

			setProcessingProgress(statusData.progress);

		}

		if (statusData?.pages_total != null) {

			setPagesTotal(statusData.pages_total);

		}

		if (statusData?.pages_extracted != null) {

			setPagesExtracted(statusData.pages_extracted);

		}

	}, []);



	const loadFailedJob = useCallback(async (targetJobId, fallbackMessage) => {

		const failedJob = await getLlmsTxtJob(currentWebsiteId, targetJobId, {

			includeOutput: false,

		});

		if (failedJob.ok && failedJob.job) {

			setJob(failedJob.job);

			setPhase("failed");

			setError(fallbackMessage ?? null);

			return true;

		}

		return false;

	}, [currentWebsiteId]);



	const loadJobFlow = useCallback(

		async (targetJobId) => {

			if (!targetJobId) {

				setJob(null);

				setPhase("idle");

				setInitialLoading(false);

				return;

			}



			if (!currentWebsiteId) {

				return;

			}



			setInitialLoading(true);

			setError(null);

			setJob(null);



			try {

				const previewJob = await getLlmsTxtJob(currentWebsiteId, targetJobId, {

					includeOutput: false,

				});



				if (!previewJob.ok || !previewJob.job) {

					setError(previewJob.message || "Job not available.");

					setPhase("error");

					return;

				}



				const currentJob = previewJob.job;

				setProcessingStatus(currentJob.status ?? "pending");

				setProcessingLabel(currentJob.status_label || "Processing");

				setLastStage(

					currentJob.output?.stage_log?.slice(-1)?.[0]?.stage ?? null

				);



				if (isLlmsTxtJobFailed(currentJob.status, currentJob.error_message)) {

					setJob(currentJob);

					setPhase("failed");

					return;

				}



				if (!isLlmsTxtJobComplete(currentJob.status)) {

					setPhase("processing");



					const poll = await pollLlmsTxtJob(currentWebsiteId, targetJobId, {

						onTick: applyStatusTick,

					});



					if (!poll.ok) {

						const loaded = await loadFailedJob(

							targetJobId,

							poll.message || poll.status?.error || "Job failed"

						);

						if (!loaded) {

							setError(poll.message || "Job failed");

							setPhase("error");

						}

						return;

					}

				}



				setPhase("loading");



				const fullJob = await getLlmsTxtJob(currentWebsiteId, targetJobId, {

					includeOutput: true,

				});

				if (!fullJob.ok || !fullJob.job) {

					setError(fullJob.message || "Job not available.");

					setPhase("error");

					return;

				}



				if (isLlmsTxtJobFailed(fullJob.job.status, fullJob.job.error_message)) {

					setJob(fullJob.job);

					setPhase("failed");

					return;

				}



				setJob(fullJob.job);

				setPhase("ready");

			} catch {

				setError("Failed to load job.");

				setPhase("error");

			} finally {

				setInitialLoading(false);

			}

		},

		[currentWebsiteId, applyStatusTick, loadFailedJob]

	);



	useEffect(() => {

		if (userLoader || websiteLoader) return;

		if (!jobId) {

			setPhase("idle");

			setInitialLoading(false);

			return;

		}

		if (!currentWebsiteId) return;

		loadJobFlow(jobId);

	}, [jobId, currentWebsiteId, loadJobFlow, userLoader, websiteLoader]);



	if (userLoader || websiteLoader) {

		return <DetailSkeleton />;

	}



	if (!jobId) {

		return (

			<div className="contentBlock ai-visibility-dashboard llms-txt">

				<p className="text-muted">No job selected.</p>

				<Button color="primary" onClick={() => navigate("/aivisibility/llms-txt")}>

					Back to LLM Discovery

				</Button>

			</div>

		);

	}



	if (jobId && !currentWebsiteId && phase !== "error" && phase !== "failed") {

		return <DetailSkeleton />;

	}



	if (initialLoading && phase === "idle") {

		return <DetailSkeleton />;

	}



	if (phase === "processing") {

		return (

			<LlmsTxtProcessing

				status={processingStatus}

				statusLabel={processingLabel}

				lastStage={lastStage}

				progress={processingProgress}

				pagesTotal={pagesTotal}

				pagesExtracted={pagesExtracted}

			/>

		);

	}



	if (phase === "loading") {

		return (

			<div className="contentBlock ai-visibility-dashboard llms-txt llms-txt-view">

				<DetailSkeleton />

			</div>

		);

	}



	if (phase === "failed" && job) {

		return (

			<div className="contentBlock ai-visibility-dashboard llms-txt llms-txt-view">

				<LlmsTxtHeader

					status={job.status}

					statusLabel={job.status_label}

				/>

				<FailedJobPanel job={job} fallbackMessage={error} />

				<div className="mt-4">

					<Button color="primary" onClick={() => navigate("/aivisibility/llms-txt")}>

						Back to LLM Discovery

					</Button>

				</div>

			</div>

		);

	}



	if (phase === "error") {

		return (

			<div className="contentBlock ai-visibility-dashboard llms-txt llms-txt-view">

				<p className="text-muted">{error || "Job not available."}</p>

				<Button color="primary" onClick={() => navigate("/aivisibility/llms-txt")}>

					Back to LLM Discovery

				</Button>

			</div>

		);

	}



	if (!job) {

		return <DetailSkeleton />;

	}



	return (

		<div className="contentBlock ai-visibility-dashboard llms-txt llms-txt-view">

			<LlmsTxtHeader

				status={job.status}

				statusLabel={job.status_label}

			/>



			{job.warning_message && (

				<Alert color="warning" className="llms-txt__warning mt-3 mb-0">

					<i className="fas fa-exclamation-triangle me-2" />

					{job.warning_message}

				</Alert>

			)}



			<div className="llms-txt__output-wrap mt-4">

				<LlmsTxtOutputView output={job.output} />

			</div>

		</div>

	);

};



export default AiVisibilityLlmsTxtView;

